import React, { useState } from 'react'
import styles from './MicrositePageMultiSelect.module.css'
import { Check, ChevronDown, Globe } from 'lucide-react'

/**
 * MicrositePageMultiSelect
 * Multi-select dropdown for choosing which pages a popup or
 * announcement should appear on. `null`/empty selection means
 * "all pages" — matches the showOnPages API contract where
 * null = all pages.
 *
 * Usage:
 *   <MicrositePageMultiSelect
 *     label="Show on pages"
 *     value={form.showOnPages} // null or ['/' , '/about-us']
 *     onChange={(v) => updateField('showOnPages', v)}
 *     availablePages={[
 *       { slug: '/', label: 'Home' },
 *       { slug: '/about-us', label: 'About us' }
 *     ]}
 *   />
 */
const MicrositePageMultiSelect = ({ label, value, onChange, availablePages = [] }) => {
    const [open, setOpen] = useState(false)
    const isAllPages = !value || value.length === 0

    const togglePage = (slug) => {
        if (isAllPages) {
            onChange?.([slug])
            return
        }
        if (value.includes(slug)) {
            const next = value.filter((s) => s !== slug)
            onChange?.(next.length === 0 ? null : next)
        } else {
            onChange?.([...value, slug])
        }
    }

    const selectAll = () => {
        onChange?.(null)
        setOpen(false)
    }

    return (
        <div className={styles.field}>
            {label && <label className={styles.label}>{label}</label>}

            <button type="button" className={styles.trigger} onClick={() => setOpen((o) => !o)}>
                <Globe size={13} className={styles.triggerIcon} />
                <span className={styles.triggerText}>
                    {isAllPages ? 'All pages' : `${value.length} page${value.length === 1 ? '' : 's'} selected`}
                </span>
                <ChevronDown size={14} className={styles.chevron} />
            </button>

            {open && (
                <div className={styles.dropdown}>
                    <button type="button" className={styles.option} onClick={selectAll}>
                        <span className={styles.checkbox}>{isAllPages && <Check size={11} />}</span>
                        All pages
                    </button>
                    <div className={styles.divider} />
                    {availablePages.map((page) => {
                        const checked = !isAllPages && value.includes(page.slug)
                        return (
                            <button
                                key={page.slug}
                                type="button"
                                className={styles.option}
                                onClick={() => togglePage(page.slug)}
                            >
                                <span className={`${styles.checkbox} ${checked ? styles.checkboxChecked : ''}`}>
                                    {checked && <Check size={11} />}
                                </span>
                                <span className={styles.optionLabel}>{page.label}</span>
                                <span className={styles.optionSlug}>{page.slug}</span>
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export default MicrositePageMultiSelect